export const SOCIAL_NETWORKS = ['instagram', 'vk', 'telegram', 'max'] as const;

export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];

export type ProfileSocialLinks = Record<SocialNetwork, string>;

export const EMPTY_SOCIAL_LINKS: ProfileSocialLinks = {
  instagram: '',
  vk: '',
  telegram: '',
  max: '',
};

type SocialLinkRule = {
  label: string;
  hosts: readonly string[];
  canonicalHost: string;
  placeholder: string;
};

const SOCIAL_LINK_RULES: Record<SocialNetwork, SocialLinkRule> = {
  instagram: {
    label: 'Instagram',
    hosts: ['instagram.com'],
    canonicalHost: 'instagram.com',
    placeholder: 'https://instagram.com/username',
  },
  vk: {
    label: 'ВКонтакте',
    hosts: ['vk.com', 'm.vk.com', 'vk.ru', 'm.vk.ru'],
    canonicalHost: 'vk.com',
    placeholder: 'https://vk.com/username',
  },
  telegram: {
    label: 'Telegram',
    hosts: ['t.me', 'telegram.me'],
    canonicalHost: 't.me',
    placeholder: 'https://t.me/username',
  },
  max: {
    label: 'MAX',
    hosts: ['max.ru', 'go.max.ru'],
    canonicalHost: 'max.ru',
    placeholder: 'https://max.ru/username',
  },
};

const INSTAGRAM_RESERVED = new Set([
  'p',
  'reel',
  'reels',
  'stories',
  'explore',
  'accounts',
  'direct',
  'tv',
  'about',
  'legal',
  'directory',
]);

const VK_CONTENT_PREFIXES = [
  'wall',
  'photo',
  'video',
  'album',
  'clip',
  'clips',
  'topic',
  'market',
  'doc',
  'page',
  'app',
  'apps',
  'feed',
  'im',
  'away',
  'search',
  'write',
  'fav',
  'settings',
  'audios',
  'audio',
  'videos',
  'friends',
];

const TELEGRAM_RESERVED = new Set(['share', 'iv', 'proxy', 'socks', 'addstickers', 'login', 's']);

const PROFILE_SEGMENT = /^[A-Za-z0-9._]{1,64}$/;
const TELEGRAM_INVITE = /^\+[A-Za-z0-9_-]{8,}$/;
const MAX_SEGMENT = /^[A-Za-z0-9._-]{1,64}$/;

export const getSocialLinkPlaceholder = (network: SocialNetwork) =>
  SOCIAL_LINK_RULES[network].placeholder;

export const getSocialLinkLabel = (network: SocialNetwork) => SOCIAL_LINK_RULES[network].label;

const stripWww = (hostname: string) => (hostname.startsWith('www.') ? hostname.slice(4) : hostname);

const getPathSegments = (pathname: string) =>
  pathname
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);

const isVkContentSegment = (segment: string) => {
  const normalized = segment.toLowerCase();
  return VK_CONTENT_PREFIXES.some((prefix) => {
    if (normalized === prefix) {
      return true;
    }
    if (normalized.startsWith(`${prefix}-`) || normalized.startsWith(`${prefix}_`)) {
      return true;
    }
    return normalized.startsWith(prefix) && /^\d/.test(normalized.slice(prefix.length));
  });
};

const isProfilePath = (network: SocialNetwork, segments: string[]) => {
  if (segments.length === 0) {
    return false;
  }

  if (network === 'instagram') {
    return (
      segments.length === 1 &&
      PROFILE_SEGMENT.test(segments[0]) &&
      !INSTAGRAM_RESERVED.has(segments[0].toLowerCase())
    );
  }

  if (network === 'vk') {
    const [segment] = segments;
    return segments.length === 1 && PROFILE_SEGMENT.test(segment) && !isVkContentSegment(segment);
  }

  if (network === 'telegram') {
    if (segments.length === 1) {
      const [segment] = segments;
      return (
        (PROFILE_SEGMENT.test(segment) && !TELEGRAM_RESERVED.has(segment.toLowerCase())) ||
        TELEGRAM_INVITE.test(segment)
      );
    }
    if (segments.length === 2 && segments[0].toLowerCase() === 'joinchat') {
      return /^[A-Za-z0-9_-]{8,}$/.test(segments[1]);
    }
    if (segments.length === 2 && segments[0].toLowerCase() === 's') {
      return PROFILE_SEGMENT.test(segments[1]) && !TELEGRAM_RESERVED.has(segments[1].toLowerCase());
    }
    return false;
  }

  if (segments.length === 1) {
    return MAX_SEGMENT.test(segments[0]);
  }

  return (
    segments.length === 2 &&
    (segments[0].toLowerCase() === 'u' || segments[0].toLowerCase() === 'join') &&
    MAX_SEGMENT.test(segments[1])
  );
};

const canonicalizePath = (network: SocialNetwork, segments: string[]) => {
  if (network === 'telegram' && segments.length === 2 && segments[0].toLowerCase() === 's') {
    return `/${segments[1]}`;
  }
  return `/${segments.join('/')}`;
};

export type SocialLinkValidation = { ok: true; value: string } | { ok: false; message: string };

export const validateSocialLink = (
  network: SocialNetwork,
  rawValue: string,
): SocialLinkValidation => {
  const value = rawValue.trim();
  if (!value) {
    return { ok: true, value: '' };
  }

  const rule = SOCIAL_LINK_RULES[network];

  if (!value.startsWith('https://')) {
    return { ok: false, message: 'Ссылка должна начинаться с https://' };
  }

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return { ok: false, message: 'Введите полную ссылку на профиль или канал' };
  }

  if (url.protocol !== 'https:' || url.username || url.password || url.port) {
    return { ok: false, message: 'Ссылка должна начинаться с https://' };
  }

  const hostname = stripWww(url.hostname.toLowerCase());
  if (!rule.hosts.includes(hostname)) {
    return {
      ok: false,
      message: `Допустимый адрес: ${rule.hosts.join(' или ')}`,
    };
  }

  const segments = getPathSegments(decodeURIComponent(url.pathname));
  if (!isProfilePath(network, segments)) {
    return { ok: false, message: 'Ссылка должна вести на профиль или канал' };
  }

  const normalized = `https://${rule.canonicalHost}${canonicalizePath(network, segments)}`;
  return { ok: true, value: normalized };
};

export const readProfileSocialLinks = (
  source:
    | {
        instagramId?: string | null;
        vkId?: string | null;
        telegramId?: string | null;
        maxId?: string | null;
      }
    | null
    | undefined,
): ProfileSocialLinks => ({
  instagram: source?.instagramId?.trim() ?? '',
  vk: source?.vkId?.trim() ?? '',
  telegram: source?.telegramId?.trim() ?? '',
  max: source?.maxId?.trim() ?? '',
});

export const toSocialLinkPayload = (links: ProfileSocialLinks) => ({
  instagramId: links.instagram || null,
  vkId: links.vk || null,
  telegramId: links.telegram || null,
  maxId: links.max || null,
});
